import { useEffect, useState, useRef, useMemo } from 'react';
import { OnProgressProps } from 'react-player/base';
import ReactPlayer from 'react-player/file';
import cm from 'classnames';

import { isDev } from 'helpers/_helpers';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { getInternalPlayer, getVideoUrl, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { setCurrentPlayerDuration } from 'components/app/editor/timestamp/_mediator/timestamp.slice';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useSbContext } from 'components/app/storyboard/_mediator/sb.provider';
import { useSbLogger } from 'components/app/storyboard/_mediator/use-sb-logger';
import { SB_EVENT } from 'components/app/storyboard/_mediator/sb-const';

import { PlaybackElement } from './_mediator/sb-player.slice';
import { onInitEnum, useSbPlayerContext } from './_mediator/sb-player.provider';
import { useLoaderBuffer } from './lib/use-loader-buffer';
import { useLoaderReady } from './lib/use-loader-ready';

import styles from './_sb-player.module.scss';

/*
  SCHEME: onInit -> (onSeeking -> onSeek) -> onReady  
*/

interface Props {
  index: Index;
  volume: number;
  playbackElement: PlaybackElement;
  payload: PlayerPayload;
  playerRef: React.RefObject<ReactPlayer>;
  setInitPlayer: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface PlayerPayload {
  isActive: boolean;
  isDamping: boolean;
  clipId?: ClipId;
  clipData?: Clip;
}

export const PROGRESS_INTERVAL: Milliseconds = 1000;

export const SbPlayerVideo = ({ index, playbackElement, payload, playerRef, setInitPlayer, volume }: Props) => {
  const dragger = useAppSelector((state) => state.app.dragger);
  const files = useAppSelector((state) => state.app__files.files);
  const sbClipPlaybackMap = useAppSelector((state) => state.sb.sbClipPlaybackMap);
  const { sbListenClip } = useSbContext();
  const { cell, track } = useAppSelector((state) => state.editor__controls.sbPosition);
  const { sbPlayers, readyToPlay, onInitParam, sbPlayerSeekTo } = useSbPlayerContext();
  const { isIdle, sbLoaderListener, playerSeekEnabled } = useAppSelector((state) => state.sb);
  const { isActive, isDamping, clipId, clipData } = payload;
  const { wsGetCursorTime } = useAudioTrackContext();
  const { sbSeekTo, sbSelectClip } = useSbCb();
  const dispatch = useAppDispatch();

  const [ended, setEnded] = useState(false);
  const [url, setUrl] = useState<string>();

  const fileId = useRef<FileId>(0);
  const duration = useRef(0);
  const seek = useRef(false);

  const logger = useSbLogger()?.player(index, clipId);

  const loader = {
    buffer: useLoaderBuffer(index, payload, clipId),
    ready: useLoaderReady(index, payload, clipId),
  };

  const playbackParams = useMemo(() => {
    if (!clipId || !sbClipPlaybackMap) return;
    return selectSbClipPlaybackParams(clipId, sbClipPlaybackMap);
  }, [sbClipPlaybackMap && clipId && sbClipPlaybackMap[clipId]]);

  /* callbacks */

  const setActivePlayerDuration = () => {
    if (!isActive) return;
    dispatch(setCurrentPlayerDuration(duration.current));
  };

  const handleInitSeek = () => {
    if (!playbackParams) return;
    if (readyToPlay.current[index] && seek.current) return;
    if (isActive) {
      switch (onInitParam.current) {
        case onInitEnum.onSelect:
          sbSelectClip(clipId!, cell, track);
          break;
        case onInitEnum.onSeek:
          sbSeekTo(wsGetCursorTime());
          break;
        case onInitEnum.onCellResize: {
          sbPlayerSeekTo(index, sbListenClip.current ? playbackParams.startTime : playbackParams.endTime);
          sbListenClip.current = null;
          break;
        }
      }
    } else if (isDamping) {
      if (onInitParam.current === onInitEnum.onCellResize) {
        sbPlayerSeekTo(index, playbackParams.endTime);
      } else sbSeekTo(wsGetCursorTime());
    } else sbPlayerSeekTo(index, playbackParams.startTime);
  };

  const onInit = (dur: Seconds) => {
    duration.current = dur;
    document.dispatchEvent(new CustomEvent(SB_EVENT, { detail: { index, clipId } }));
    setActivePlayerDuration();
    setInitPlayer(true);
    handleInitSeek();
  };

  const onProgress = ({ loadedSeconds, playedSeconds }: OnProgressProps) => {
    if (!seek.current) return;
    const payload = {
      duration: duration.current,
      loaded: loadedSeconds - playedSeconds,
      played: playedSeconds,
    };
    if (loader.buffer.listen.current) loader.buffer.onProgress(payload);
    else if (!readyToPlay.current[index]) {
      if (isActive) {
        loader.ready.cancel();
        loader.buffer.switchReadyToBuffer(payload);
      } else loader.ready.onProgress(payload);
    }
  };

  const onSeeking = () => {
    seek.current = false;
  };

  const onSeek = (time: Seconds) => {
    if (!duration.current) {
      duration.current = getInternalPlayer(playerRef.current)?.duration ?? 0;
      console.log('sb-player-element: EXCEPTION');
    }
    if (loader.buffer.listen.current) loader.buffer.start();
    setEnded(time - duration.current >= 0);
    seek.current = true;
    logger?.onSeek();
  };

  const onReady = () => {
    if (!clipId) return;
    if (!seek.current || dragger || loader.buffer.listen.current) return;
    loader.ready.onReady();
  };

  /* effects */

  useEffect(() => {
    sbPlayers.current[index] = playerRef.current;
    return () => {
      sbPlayers.current[index] = null;
      loader.buffer.cancel();
      loader.ready.cancel();
    };
  }, []);

  useEffect(() => {
    setActivePlayerDuration();
  }, [isActive]);

  useEffect(() => {
    readyToPlay.current[index] = false;
    seek.current = false;
    loader.ready.cancel();
  }, [playbackElement]);

  useEffect(() => {
    if (!clipData) {
      duration.current = 0;
      fileId.current = 0;
      setEnded(false);
      setUrl('');
      return;
    }
    if (clipData.mediaFileId === fileId.current) return;

    setInitPlayer(false);
    setUrl(getVideoUrl(files[clipData.mediaFileId]));
    fileId.current = clipData.mediaFileId;
    duration.current = 0;
  }, [clipData]);

  useEffect(() => {
    if (readyToPlay.current[index]) return;
    onReady();
  }, [dragger]);

  useEffect(() => {
    return () => {
      if (loader.buffer.listen.current) loader.buffer.cancel();
    };
  }, [sbLoaderListener]);

  useEffect(() => {
    if (!playbackParams) return;
    if (isActive || isDamping) return;
    setTimeout(() => sbPlayerSeekTo(index, playbackParams.startTime), 0);
    loader.ready.cancel();
  }, [playbackParams, isDamping, isActive]);

  return (
    <div
      className={cm(styles.player__video, {
        [styles['player__video--notPlaying']]: ended || (isActive && isIdle),
        [styles['player__video--idle']]: isActive && isIdle,
        [styles['player__video--ended']]: ended,
      })}
    >
      <ReactPlayer
        ref={playerRef}
        muted={isDev()}
        volume={volume}
        onReady={onReady}
        onBuffer={loader.buffer.onBuffer}
        onProgress={onProgress}
        progressInterval={PROGRESS_INTERVAL}
        onEnded={() => setEnded(true)}
        playbackRate={playbackParams?.rate}
        className={styles.player__react}
        config={{ attributes: { crossOrigin: 'anonymous' } }}
        controls={playerSeekEnabled}
        onSeeking={onSeeking}
        onDuration={onInit}
        onSeek={onSeek}
        url={url}
      />
    </div>
  );
};
